import { LogOut, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import RenderProgressIndicator from './RenderProgressIndicator';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import { canAccessReservations, isAdmin, isReservationOnly } from '../lib/roles';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{
    key: string;
    name: string;
    description: string;
  }>;
}

interface HeaderProps {
  user: User | null;
  currentPage?: string;
}

interface NavigationItem {
  name: string;
  href: string;
  key: string;
}

const allNavigationItems: NavigationItem[] = [
  { name: 'Dashboard', href: '/dashboard', key: 'dashboard' },
  { name: 'Orders', href: '/orders', key: 'orders' },
  { name: 'Jobs', href: '/jobs', key: 'jobs' },
  { name: 'Tasks', href: '/tasks', key: 'tasks' },
  { name: 'Shipments', href: '/shipments', key: 'shipments' },
  { name: 'Categories', href: '/categories', key: 'categories' },
  { name: 'Suppliers', href: '/suppliers', key: 'suppliers' },
  { name: 'QR Codes', href: '/qr-codes', key: 'qr-codes' },
  { name: 'Quotes', href: '/quotes', key: 'quotes' },
  { name: 'Restyle', href: '/restyle', key: 'restyle' },
  // Settings rendered separately with a submenu
];

const productPages = ['assets', 'assets-pre-loved', 'assets-new'] as const;
const isProductPage = (page: string) => (productPages as readonly string[]).includes(page);

export default function Header({ user, currentPage = 'dashboard' }: HeaderProps) {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const reservationOnly = isReservationOnly(user);
  const showReservations = canAccessReservations(user);
  const navigationItems = reservationOnly ? [] : allNavigationItems;

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const getUserInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return `${first}${last}`.toUpperCase() || 'U';
  };

  const handleNavigation = (href: string) => {
    navigate(href);
  };

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="container mx-auto px-4">
        {/* Top section with logo, mobile toggle and user */}
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <img
              src="/homebound_logo.png"
              alt="homebound"
              className="h-8 w-auto cursor-pointer"
              onClick={() => handleNavigation('/dashboard')}
            />
            {/* Mobile menu toggle */}
            <button
              className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:bg-gray-50"
              aria-label="Open main menu"
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            {/* Render Progress Indicator */}
            <RenderProgressIndicator />

            {/* Notifications */}
            <NotificationBell />

            {/* User Profile */}
            <div className="flex items-center space-x-3">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary text-primary-foreground font-heading font-semibold text-xs">
                  {user ? getUserInitials(user.first_name, user.last_name) : 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="text-right">
                <p className="text-sm font-heading font-medium text-foreground">
                  {user?.first_name} {user?.last_name}
                </p>
              </div>
            </div>

            {/* Logout Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="font-heading"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="border-t border-gray-100 hidden md:block">
          <div className="flex items-center space-x-6 py-2">
            {navigationItems.map((item) => (
              <button
                key={item.key}
                onClick={() => handleNavigation(item.href)}
                className={`text-sm font-medium transition-colors px-3 py-1.5 rounded-md hover:bg-gray-50 ${
                  currentPage === item.key
                    ? 'text-gray-900 hover:text-primary bg-nav-highlight'
                    : 'text-gray-600 hover:text-primary'
                }`}
              >
                {item.name}
              </button>
            ))}

            {showReservations && (
              <button
                onClick={() => handleNavigation('/reservations')}
                className={`text-sm font-medium transition-colors px-3 py-1.5 rounded-md hover:bg-gray-50 ${
                  currentPage === 'reservations'
                    ? 'text-gray-900 hover:text-primary bg-nav-highlight'
                    : 'text-gray-600 hover:text-primary'
                }`}
              >
                Reservations
              </button>
            )}

            {/* Products with submenu */}
            <div className="relative group">
              <button
                type="button"
                aria-haspopup="true"
                className={`text-sm font-medium transition-colors px-3 py-1.5 rounded-md hover:bg-gray-50 ${
                  isProductPage(currentPage)
                    ? 'text-gray-900 hover:text-primary bg-nav-highlight'
                    : 'text-gray-600 hover:text-primary'
                }`}
              >
                Products
              </button>
              <div className="absolute left-0 top-full w-48 rounded-md border bg-white py-2 shadow-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none group-hover:pointer-events-auto z-50">
                <button
                  onClick={() => handleNavigation('/assets')}
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    currentPage === 'assets' ? 'text-gray-900' : 'text-gray-600'
                  }`}
                >
                  All Products
                </button>
                <button
                  onClick={() => handleNavigation('/assets/pre-loved')}
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    currentPage === 'assets-pre-loved' ? 'text-gray-900' : 'text-gray-600'
                  }`}
                >
                  Pre-Loved
                </button>
                <button
                  onClick={() => handleNavigation('/assets/new')}
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    currentPage === 'assets-new' ? 'text-gray-900' : 'text-gray-600'
                  }`}
                >
                  New Products
                </button>
              </div>
            </div>

            {/* Settings with submenu — no /settings route; clicking parent used to hit * → login → dashboard */}
            {!reservationOnly && <div className="relative group">
              <button
                type="button"
                aria-haspopup="true"
                className={`text-sm font-medium transition-colors px-3 py-1.5 rounded-md hover:bg-gray-50 ${
                  currentPage === 'settings' || currentPage === 'users' || currentPage === 'locations' || currentPage === 'task-templates' || currentPage === 'room-types' || currentPage === 'shopify-sync' || currentPage === 'supercycle-subscription-push' || currentPage === 'break-even'
                    ? 'text-gray-900 hover:text-primary bg-nav-highlight'
                    : 'text-gray-600 hover:text-primary'
                }`}
              >
                Settings
              </button>
              <div className="absolute left-0 top-full w-48 rounded-md border bg-white py-2 shadow-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none group-hover:pointer-events-auto z-50">
                <button
                  onClick={() => handleNavigation('/settings/locations')}
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    currentPage === 'locations' ? 'text-gray-900' : 'text-gray-600'
                  }`}
                >
                  Locations
                </button>
                <button
                  onClick={() => handleNavigation('/users')}
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    currentPage === 'users' ? 'text-gray-900' : 'text-gray-600'
                  }`}
                >
                  Users
                </button>
                <button
                  onClick={() => handleNavigation('/task-templates')}
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    currentPage === 'task-templates' ? 'text-gray-900' : 'text-gray-600'
                  }`}
                >
                  Task Templates
                </button>
                <button
                  onClick={() => handleNavigation('/settings/shopify-sync')}
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    currentPage === 'shopify-sync' ? 'text-gray-900' : 'text-gray-600'
                  }`}
                >
                  Shopify Sync
                </button>
                <button
                  onClick={() => handleNavigation('/settings/supercycle-subscription-push')}
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    currentPage === 'supercycle-subscription-push' ? 'text-gray-900' : 'text-gray-600'
                  }`}
                >
                  Supercycle sync
                </button>
                <button
                  onClick={() => handleNavigation('/settings/room-types')}
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    currentPage === 'room-types' ? 'text-gray-900' : 'text-gray-600'
                  }`}
                >
                  Room Types
                </button>
                <button
                  onClick={() => handleNavigation('/settings/notifications')}
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    currentPage === 'notification-preferences' ? 'text-gray-900' : 'text-gray-600'
                  }`}
                >
                  Notifications
                </button>
                {user && isAdmin(user) && (
                  <button
                    onClick={() => handleNavigation('/break-even')}
                    className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                      currentPage === 'break-even' ? 'text-gray-900' : 'text-gray-600'
                    }`}
                  >
                    Break-Even Analysis
                  </button>
                )}
              </div>
            </div>}
          </div>
        </nav>

        {/* Mobile Navigation Panel */}
        {mobileOpen && (
          <div className="md:hidden border-t border-gray-100 py-2">
            <div className="flex flex-col gap-1 p-2">
              {navigationItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => {
                    setMobileOpen(false);
                    handleNavigation(item.href);
                  }}
                  className={`w-full text-left text-sm font-medium px-3 py-2 rounded-md hover:bg-gray-50 ${
                    currentPage === item.key ? 'text-gray-900 bg-nav-highlight' : 'text-gray-600'
                  }`}
                >
                  {item.name}
                </button>
              ))}

              {showReservations && (
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleNavigation('/reservations');
                  }}
                  className={`w-full text-left text-sm font-medium px-3 py-2 rounded-md hover:bg-gray-50 ${
                    currentPage === 'reservations' ? 'text-gray-900 bg-nav-highlight' : 'text-gray-600'
                  }`}
                >
                  Reservations
                </button>
              )}

              {/* Mobile Products block */}
              <div className="mt-2">
                <div className="px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">
                  Products
                </div>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleNavigation('/assets');
                  }}
                  className={`w-full text-left text-sm font-medium px-3 py-2 rounded-md hover:bg-gray-50 ${
                    currentPage === 'assets' ? 'text-gray-900 bg-nav-highlight' : 'text-gray-600'
                  }`}
                >
                  All Products
                </button>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleNavigation('/assets/pre-loved');
                  }}
                  className={`w-full text-left text-sm font-medium px-3 py-2 rounded-md hover:bg-gray-50 ${
                    currentPage === 'assets-pre-loved' ? 'text-gray-900 bg-nav-highlight' : 'text-gray-600'
                  }`}
                >
                  Pre-Loved
                </button>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleNavigation('/assets/new');
                  }}
                  className={`w-full text-left text-sm font-medium px-3 py-2 rounded-md hover:bg-gray-50 ${
                    currentPage === 'assets-new' ? 'text-gray-900 bg-nav-highlight' : 'text-gray-600'
                  }`}
                >
                  New Products
                </button>
              </div>

              {/* Mobile Settings block */}
              {!reservationOnly && <div className="mt-2">
                <div className="px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">
                  Settings
                </div>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleNavigation('/settings/locations');
                  }}
                  className={`w-full text-left text-sm font-medium px-3 py-2 rounded-md hover:bg-gray-50 ${
                    currentPage === 'locations' ? 'text-gray-900 bg-nav-highlight' : 'text-gray-600'
                  }`}
                >
                  Locations
                </button>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleNavigation('/users');
                  }}
                  className={`w-full text-left text-sm font-medium px-3 py-2 rounded-md hover:bg-gray-50 ${
                    currentPage === 'users' ? 'text-gray-900 bg-nav-highlight' : 'text-gray-600'
                  }`}
                >
                  Users
                </button>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleNavigation('/task-templates');
                  }}
                  className={`w-full text-left text-sm font-medium px-3 py-2 rounded-md hover:bg-gray-50 ${
                    currentPage === 'task-templates' ? 'text-gray-900 bg-nav-highlight' : 'text-gray-600'
                  }`}
                >
                  Task Templates
                </button>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleNavigation('/settings/shopify-sync');
                  }}
                  className={`w-full text-left text-sm font-medium px-3 py-2 rounded-md hover:bg-gray-50 ${
                    currentPage === 'shopify-sync' ? 'text-gray-900 bg-nav-highlight' : 'text-gray-600'
                  }`}
                >
                  Shopify Sync
                </button>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleNavigation('/settings/supercycle-subscription-push');
                  }}
                  className={`w-full text-left text-sm font-medium px-3 py-2 rounded-md hover:bg-gray-50 ${
                    currentPage === 'supercycle-subscription-push' ? 'text-gray-900 bg-nav-highlight' : 'text-gray-600'
                  }`}
                >
                  Supercycle sync
                </button>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleNavigation('/settings/room-types');
                  }}
                  className={`w-full text-left text-sm font-medium px-3 py-2 rounded-md hover:bg-gray-50 ${
                    currentPage === 'room-types' ? 'text-gray-900 bg-nav-highlight' : 'text-gray-600'
                  }`}
                >
                  Room Types
                </button>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleNavigation('/settings/notifications');
                  }}
                  className={`w-full text-left text-sm font-medium px-3 py-2 rounded-md hover:bg-gray-50 ${
                    currentPage === 'notification-preferences' ? 'text-gray-900 bg-nav-highlight' : 'text-gray-600'
                  }`}
                >
                  Notifications
                </button>
                {user && isAdmin(user) && (
                  <button
                    onClick={() => {
                      setMobileOpen(false);
                      handleNavigation('/break-even');
                    }}
                    className={`w-full text-left text-sm font-medium px-3 py-2 rounded-md hover:bg-gray-50 ${
                      currentPage === 'break-even' ? 'text-gray-900 bg-nav-highlight' : 'text-gray-600'
                    }`}
                  >
                    Break-Even Analysis
                  </button>
                )}
              </div>}

              {/* Mobile user actions */}
              <div className="mt-3 border-t pt-3">
                <div className="flex items-center justify-between px-3">
                  <span className="text-sm text-muted-foreground">
                    {user?.first_name} {user?.last_name}
                  </span>
                  <Button variant="outline" size="sm" onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" /> Sign Out
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
